/* eslint-disable no-console */

import { register } from "register-service-worker";

// Function to convert base64 string to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String) {
  // Make sure the key doesn't have any whitespace
  const cleanedBase64String = base64String.trim();

  // Add padding if needed
  const padding = "=".repeat((4 - (cleanedBase64String.length % 4)) % 4);
  const base64 = (cleanedBase64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");

  try {
    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  } catch (error) {
    console.error("Error decoding base64 string:", error);
    throw new Error("Invalid VAPID public key format");
  }
}

// Store the service worker registration for later use
let swRegistration = null;
let isRegistering = false;
let registrationPromise = null;

// Function to ensure service worker is registered
function ensureServiceWorkerRegistered() {
  if (registrationPromise) {
    return registrationPromise;
  }

  if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
    console.warn("Service workers or Push API not supported in this browser");
    return Promise.reject(new Error("Service workers not supported"));
  }

  if (isRegistering) {
    // Return a promise that resolves when registration is complete
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (swRegistration) {
          clearInterval(checkInterval);
          resolve(swRegistration);
        } else if (!isRegistering) {
          clearInterval(checkInterval);
          reject(new Error("Service worker registration failed"));
        }
      }, 100);
    });
  }

  console.log("Starting service worker registration");
  isRegistering = true;
  
  registrationPromise = new Promise((resolve, reject) => {
    // register(`${import.meta.env.BASE_URL || "/"}service-worker.js`, {
    register(`${import.meta.env.BASE_URL || "/"}firebase-messaging-sw.js`, {
      ready() {
        console.log("Service worker is active");
      },
      registered(registration) {
        console.log("Service worker has been registered successfully");
        swRegistration = registration;
        window.swRegistration = registration;
        isRegistering = false;
        resolve(registration);
      },
      cached() {
        console.log("Content has been cached for offline use");
      },
      updatefound() {
        console.log("New content is downloading");
      },
      updated(registration) {
        console.log("New content is available; please refresh");
        registration.waiting?.postMessage({ type: 'SKIP_WAITING' });
      },
      offline() {
        console.log("No internet connection found. App is running in offline mode");
      },
      error(error) {
        console.error("Error during service worker registration:", error);
        isRegistering = false;
        reject(error);
      },
    });
  });

  return registrationPromise;
}

// Export functions to subscribe to push notifications
export async function subscribeToPushNotifications(vapidPublicKey) {
  try {
    const registration = await ensureServiceWorkerRegistered();
    
    // Check for existing subscription
    const subscription = await registration.pushManager.getSubscription();
    if (subscription) {
      console.log("Using existing push subscription");
      return subscription;
    }

    // Create new subscription
    const applicationServerKey = urlBase64ToUint8Array(vapidPublicKey);
    const newSubscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey,
    });

    console.log("User is subscribed to push notifications with new subscription");
    return newSubscription;
  } catch (error) {
    console.error("Failed to subscribe to push notifications:", error);
    return null;
  }
}

// Function to format subscription for backend
export function formatSubscriptionForBackend(subscription) {
  if (!subscription) return null;

  return {
    endpoint: subscription.endpoint,
    keys: {
      p256dh: btoa(
        String.fromCharCode.apply(
          null,
          new Uint8Array(subscription.getKey("p256dh"))
        )
      )
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, ""),
      auth: btoa(
        String.fromCharCode.apply(
          null,
          new Uint8Array(subscription.getKey("auth"))
        )
      )
        .replace(/\+/g, "-")
        .replace(/\//g, "_")
        .replace(/=+$/, ""),
    },
  };
}

// Initialize service worker registration when this module is imported
ensureServiceWorkerRegistered().catch(error => {
  console.error("Failed to register service worker on initial load:", error);
});
